import { createStore, useStore } from 'zustand';
import { toast } from '@skillarea/lib-frontend';
import {
  FlagsDto,
  LangDto,
  OauthDto,
  UserDeleteDto,
  UserReturnDto,
  UserUpdateDto,
  UserUpdatePasswordDto,
} from '@skillarea/lib-shared';

import i18n from '~/common/i18n';
import { UserService } from '~/services/userService';
import { IRequestOptions } from '~/services/options';
import { onboardStore } from '~/stores/onboardStore';

const service = new UserService();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getResultErrorMessage(result: unknown): string | undefined {
  if (!isRecord(result)) {
    return undefined;
  }

  if (Array.isArray(result.dtoMessages)) {
    const firstDtoMessage = result.dtoMessages.find((dtoMessage) => {
      return (
        isRecord(dtoMessage) &&
        typeof dtoMessage.message === 'string' &&
        dtoMessage.message.length > 0
      );
    });

    if (
      isRecord(firstDtoMessage) &&
      typeof firstDtoMessage.message === 'string'
    ) {
      return firstDtoMessage.message;
    }
  }

  if (typeof result.error === 'string' && result.error.length > 0) {
    if (/key\(name\).+already exists/i.test(result.error)) {
      return i18n.t('custom.nameUsed', { ns: 'error' });
    }

    return result.error;
  }

  return undefined;
}

function getResultStatus(result: unknown): number | undefined {
  if (!isRecord(result)) {
    return undefined;
  }

  return typeof result.status === 'number' ? result.status : undefined;
}

type TUserState = {
  id: number;
  name: string;
  email: string;
  avatar?: string;
  dateOfBirth?: UserReturnDto['dateOfBirth'];
  flags: FlagsDto;
  regData?: UserReturnDto['regData'];
  oauth?: OauthDto;
  settings?: UserReturnDto['settings'];
  gameStats?: UserReturnDto['gameStats'];
  minimumAge?: UserReturnDto['minimumAge'];
  isUnderMinimumAge: UserReturnDto['isUnderMinimumAge'];

  setUserData: (data: UserReturnDto) => void;
  loadUser: () => Promise<unknown>;
  saveUserData: (data: UserUpdateDto) => Promise<unknown>;
  saveUserLang: (lang: LangDto) => Promise<unknown>;
  savePassword: (
    data: UserUpdatePasswordDto,
    options?: IRequestOptions,
  ) => Promise<unknown>;
  deleteUser: (
    data: UserDeleteDto,
    options?: IRequestOptions,
  ) => Promise<unknown>;
};

export const userStore = createStore<TUserState>((set, get) => ({
  id: 0,
  name: '',
  email: '',
  avatar: undefined,
  dateOfBirth: undefined,
  flags: {} as FlagsDto,
  regData: undefined,
  oauth: undefined,
  minimumAge: undefined,
  isUnderMinimumAge: false,

  setUserData(data: UserReturnDto) {
    set({
      id: data.id,
      name: data.name,
      email: data.email,
      avatar: data.avatar,
      dateOfBirth: data.dateOfBirth,
      flags: data.flags,
      regData: data.regData,
      oauth: data.oauth,
      settings: data.settings,
      gameStats: data.gameStats,
      minimumAge: data.minimumAge,
      isUnderMinimumAge: data.isUnderMinimumAge,
    });
  },

  async loadUser() {
    if (!onboardStore.getState().isLoggedIn) return;
    const response = await service.self();
    if (response?.success && response?.data) {
      get().setUserData(response.data);
    }
    return response;
  },

  async saveUserData(data: UserUpdateDto) {
    const response = await service.updateSelf(
      {
        body: data,
      },
      {
        errorInterceptor: (response) => {
          if (response.status === 401 || response.status === 403) {
            return false;
          }

          return true;
        },
      },
    );

    const status = getResultStatus(response);

    if (response?.success && response?.data) {
      get().setUserData(response.data);
      toast?.success(i18n.t('userSuccess', { ns: 'account' }));
    } else if (status !== 401 && status !== 403) {
      toast?.error(
        getResultErrorMessage(response) ||
          i18n.t('saveProfileError', { ns: 'common' }),
      );
    }

    return response;
  },

  async saveUserLang(lang: LangDto) {
    const response = await service.updateSelf(
      { body: { lang } },
      { errorInterceptor: () => true },
    );

    if (response?.success && response?.data) {
      get().setUserData(response.data);
    }

    return response;
  },

  async savePassword(
    data: UserUpdatePasswordDto,
    options: IRequestOptions = {},
  ) {
    const { confirmPassword, newPassword, oldPassword } = data;
    const response = await service.updatePassword(
      {
        body: { oldPassword, newPassword, confirmPassword },
      },
      options,
    );

    if (response.success) {
      toast?.success(i18n.t('passwordSuccess', { ns: 'account' }));
    }

    return response;
  },

  async deleteUser({ password }: UserDeleteDto, options: IRequestOptions = {}) {
    const response = await service.deleteSelf({ body: { password } }, options);

    if (response.success) {
      toast?.success(i18n.t('deleteSuccess', { ns: 'account' }));
    }

    return response;
  },
}));

export const useUserStore = <T>(selector: (s: TUserState) => T) =>
  useStore(userStore, selector);

export const useUserId = () => useUserStore((s) => s.id);
export const useUserName = () => useUserStore((s) => s.name);
export const useUserEmail = () => useUserStore((s) => s.email);
export const useUserAvatar = () => useUserStore((s) => s.avatar);
export const useUserDateOfBirth = () => useUserStore((s) => s.dateOfBirth);
export const useUserFlags = () => useUserStore((s) => s.flags);
export const useUserRegData = () => useUserStore((s) => s.regData);
export const useRequiresDateOfBirth = () =>
  useUserStore((s) =>
    Boolean(
      s.id && (!s.dateOfBirth || s.regData?.requiresDateOfBirth === true),
    ),
  );
export const useUserOauth = () => useUserStore((s) => s.oauth);
export const useUserSettings = () => useUserStore((s) => s.settings);
export const useUserGameStats = () => useUserStore((s) => s.gameStats);
export const useUserMinimumAge = () => useUserStore((s) => s.minimumAge);
export const useIsUserUnderMinimumAge = () =>
  useUserStore((s) => s.isUnderMinimumAge);
export const useUserMfaEnabled = () =>
  useUserStore((s) => Boolean(s.settings?.mfaEnabled));
export const useSaveUserData = () => useUserStore((s) => s.saveUserData);
export const useSavePassword = () => useUserStore((s) => s.savePassword);
